import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DebtorService, DebtorPageDto, DebtorSearchCriteria } from '../debtor.service';
import { DebtorDetailDto, DebtorKpiDto, DebtorSearchResultDto } from '../models/debtor.model';

interface ApiPage<T> { content?: T[]; items?: T[]; totalElements?: number; total?: number; }

/** Backend projection. Optional fields keep the adapter compatible with deployed DTO versions. */
export interface DebtorApiDto {
  id?: string | number; debtorId?: string | number; externalReference?: string; reference?: string;
  fullName?: string; companyName?: string; name?: string; firstName?: string; lastName?: string;
  phone?: string; mainPhone?: string; email?: string; legalIdentifier?: string; cin?: string; ice?: string;
  city?: string; creditorId?: string | number; creditorLabel?: string; clientType?: string;
  riskSegment?: string; riskScore?: number; activeCaseCount?: number; activeCasesCount?: number;
  outstandingAmount?: number; totalDebtAmount?: number; totalOverdueAmount?: number; overdueAmount?: number;
  hasDuplicateAlert?: boolean; duplicateAlertType?: string; status?: string; [key: string]: unknown;
}

export interface DebtorDetailApiDto extends DebtorApiDto {
  legalForm?: string; address?: string; postalCode?: string; country?: string; contactPersonName?: string;
  segment?: string; pdScore?: number; lgdScore?: number; kpi?: Partial<DebtorKpiDto>;
  contracts?: unknown[]; debts?: unknown[]; interactions?: unknown[]; documents?: unknown[]; guarantees?: unknown[];
}

@Injectable()
export class DebtorApiService extends DebtorService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/debtors`;

  search(criteria: DebtorSearchCriteria): Observable<DebtorPageDto> {
    let params = new HttpParams().set('page', String(criteria.page ?? 0)).set('size', String(criteria.size ?? 20));
    if (criteria.query?.trim()) params = params.set('query', criteria.query.trim());
    for (const key of ['clientType', 'status', 'riskSegment', 'city', 'creditorId', 'sort'] as const) {
      const value = criteria[key];
      if (value) params = params.set(key, value);
    }
    return this.http.get<ApiPage<DebtorApiDto> | DebtorApiDto[]>(this.base, { params }).pipe(
      map(response => {
        const page = Array.isArray(response) ? { content: response, total: response.length } : response;
        const content = page.content ?? page.items ?? [];
        return { total: page.totalElements ?? page.total ?? content.length, items: content.map(mapDebtorSearchDto) };
      }),
    );
  }

  getDebtor(debtorId: string): Observable<DebtorDetailDto> {
    return this.http.get<DebtorDetailApiDto>(`${this.base}/${encodeURIComponent(debtorId)}`).pipe(map(mapDebtorDetailDto));
  }
}

export function mapDebtorSearchDto(dto: DebtorApiDto): DebtorSearchResultDto {
  const debtorId = String(dto.debtorId ?? dto.id ?? '');
  return {
    debtorId, debtorName: debtorName(dto), externalReference: stringValue(dto.externalReference ?? dto.reference),
    contractReferences: arrayOfStrings(dto['contractReferences']), clientType: stringValue(dto.clientType),
    ice: stringValue(dto.ice ?? dto.legalIdentifier), cin: stringValue(dto.cin), mainPhone: stringValue(dto.mainPhone ?? dto.phone),
    email: stringValue(dto.email), city: stringValue(dto.city), creditorId: String(dto.creditorId ?? ''),
    creditorLabel: stringValue(dto.creditorLabel), riskSegment: stringValue(dto.riskSegment), riskScore: numberValue(dto.riskScore),
    outstandingAmount: numberValue(dto.outstandingAmount ?? dto.totalDebtAmount), activeCasesCount: numberValue(dto.activeCasesCount ?? dto.activeCaseCount),
    totalOverdueAmount: numberValue(dto.totalOverdueAmount ?? dto.overdueAmount), hasDuplicateAlert: Boolean(dto.hasDuplicateAlert),
    duplicateAlertType: stringValue(dto.duplicateAlertType), status: stringValue(dto.status),
  };
}

export function mapDebtorDetailDto(dto: DebtorDetailApiDto): DebtorDetailDto {
  const search = mapDebtorSearchDto(dto); const kpi = dto.kpi ?? {};
  return {
    ...search, legalForm: stringValue(dto.legalForm), address: stringValue(dto.address), postalCode: stringValue(dto.postalCode),
    country: stringValue(dto.country), contactPersonName: stringValue(dto.contactPersonName), segment: stringValue(dto.segment),
    pdScore: optionalNumber(dto.pdScore), lgdScore: optionalNumber(dto.lgdScore),
    kpi: { activeCasesCount: numberValue(kpi.activeCasesCount ?? dto.activeCasesCount ?? dto.activeCaseCount), totalDebtAmount: numberValue(kpi.totalDebtAmount ?? dto.totalDebtAmount ?? dto.outstandingAmount), totalOverdueAmount: numberValue(kpi.totalOverdueAmount ?? dto.totalOverdueAmount ?? dto.overdueAmount), averageDaysLate: numberValue(kpi.averageDaysLate), brokenPromisesCount: numberValue(kpi.brokenPromisesCount), riskScore: numberValue(kpi.riskScore ?? dto.riskScore) },
    nextAction: undefined, contracts: (dto.contracts ?? []) as DebtorDetailDto['contracts'], debts: (dto.debts ?? []) as DebtorDetailDto['debts'],
    interactions: (dto.interactions ?? []) as DebtorDetailDto['interactions'], documents: (dto.documents ?? []) as DebtorDetailDto['documents'], guarantees: (dto.guarantees ?? []) as DebtorDetailDto['guarantees'],
  };
}

function debtorName(dto: DebtorApiDto): string { return stringValue(dto.fullName ?? dto.companyName ?? dto.name ?? [dto.firstName, dto.lastName].filter(Boolean).join(' ')); }
function stringValue(value: unknown): string { return value == null ? '' : String(value); }
function numberValue(value: unknown): number { return typeof value === 'number' && Number.isFinite(value) ? value : 0; }
function optionalNumber(value: unknown): number | undefined { return typeof value === 'number' && Number.isFinite(value) ? value : undefined; }
function arrayOfStrings(value: unknown): string[] | undefined { return Array.isArray(value) ? value.filter(item => typeof item === 'string') as string[] : undefined; }
